import { getMarketingTracking } from '@openeducation/pp-js-utils';
import {
  paymentMethodOnFileAtom,
  pendingPaymentAtom,
  pendingPaymentIdAtom,
  payPalTokenAtom,
  paymentInfoAtom,
} from '@openeducation/pp-rn-jotai-atoms';
import {
  BillingAddress,
  ManualPaymentParams,
  PaymentMethod,
  PaymentMethodOnFile,
  PaymentParams,
  PaymentResponse,
  BillingAgreements,
  PaymentGateway,
  ExtendedPaymentInfoFormData,
} from '@openeducation/pp-rn-shared-types';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useCallback, useState, useRef } from 'react';

import { getPaymentParams } from '../../../helpers/get-payment-params';

type HandleSubmitFn = {
  (): void;
} | null;

interface UseHandlePaymentSubmit {
  submitPaymentReset: () => void;
  submitPayU: () => void;
  validateAddress: () => void;
  validateAddress2?: () => void;
  acceptContract?: () => void;
  submitPayPal: () => void;
  submitYuno: () => void;
  submitPayment: (params: PaymentParams) => void;
  paymentResponse?: PaymentResponse;
  pendingPaymentMethod: PaymentMethod;
  paymentInfo: ExtendedPaymentInfoFormData;
  billingAddress: BillingAddress;
  billingAddress2?: BillingAddress;
  manualPaymentParams?: ManualPaymentParams;
  isPayU: boolean;
  isYuno: boolean;
  isSubmitError: boolean;
  isPayPalRedirectionFlow: boolean;
  isUpsell?: boolean;
  contractAccepted: boolean;
  isAddressValid: boolean;
  isAddressValid2?: boolean;
  sfdcToken?: string;
  contractId: string;
  documentNumber?: string;
  documentNumber2?: string;
  documentType?: string;
  documentType2?: string;
  cpfNumber?: string;
  isScreenLoading?: boolean;
  billingAgreements?: BillingAgreements;
  alternativePaymentGateway?: PaymentGateway;
}

export const useHandlePaymentSubmit = ({
  paymentResponse,
  isPayU,
  isYuno,
  isSubmitError,
  submitPaymentReset,
  submitPayU,
  pendingPaymentMethod,
  validateAddress,
  validateAddress2,
  acceptContract,
  isPayPalRedirectionFlow,
  submitPayPal,
  submitYuno,
  paymentInfo,
  billingAddress,
  billingAddress2,
  sfdcToken,
  contractId,
  documentNumber,
  documentType,
  documentNumber2,
  documentType2,
  isUpsell,
  manualPaymentParams,
  submitPayment,
  contractAccepted,
  isAddressValid,
  isAddressValid2,
  cpfNumber,
  isScreenLoading,
  billingAgreements,
  alternativePaymentGateway,
}: UseHandlePaymentSubmit) => {
  const [triggerSubmit, setTriggerSubmit] = useState(false);
  const setPendingPaymentId = useSetAtom(pendingPaymentIdAtom);
  const setPaymentInfo = useSetAtom(paymentInfoAtom);
  const setPendingPayment = useSetAtom(pendingPaymentAtom);
  const payPalToken = useAtomValue(payPalTokenAtom);
  const selectedPaymentMethodOnFile = useAtomValue(paymentMethodOnFileAtom);
  const handleSubmitRef = useRef<HandleSubmitFn>(null);

  useEffect(() => {
    if (paymentResponse !== undefined && !isSubmitError) {
      setPendingPaymentId(paymentResponse);

      if (isPayU) {
        submitPayU();
      }
      if (isYuno) {
        submitYuno();
      }

      setPaymentInfo(null);
      submitPaymentReset();
    }
  }, [
    paymentResponse,
    setPendingPaymentId,
    isPayU,
    isYuno,
    submitPayU,
    isSubmitError,
    submitPaymentReset,
    submitYuno,
  ]);

  const onSubmitButtonPress = useCallback(() => {
    setPendingPayment(pendingPaymentMethod);
    validateAddress();

    if (validateAddress2) {
      validateAddress2();
    }

    if (acceptContract) {
      acceptContract();
    } else {
      setTriggerSubmit(true);
    }
  }, [
    setPendingPayment,
    acceptContract,
    pendingPaymentMethod,
    validateAddress,
    validateAddress2,
  ]);

  const handleSubmit = useCallback(() => {
    const isPayPalAutoSubmit = !!payPalToken && !!billingAgreements;

    if (!isPayPalAutoSubmit) {
      const isTwoCreditCards =
        paymentInfo.paymentMethod === PaymentMethod.TWO_CREDIT_CARDS;
      const areAddressesValid = isTwoCreditCards
        ? isAddressValid && (isAddressValid2 ?? true)
        : isAddressValid;

      if (!areAddressesValid) {
        return;
      }
    }

    const paymentParams: PaymentParams = getPaymentParams({
      paymentInfo,
      contractId,
      documentNumber,
      documentNumber2,
      documentType,
      documentType2,
      isUpsell,
      sfdcToken,
      billingAddress,
      billingAddress2,
      manualPayment: manualPaymentParams,
      cpfNumber,
      usePaymentMethodOnFile:
        selectedPaymentMethodOnFile === PaymentMethodOnFile.EXISTING,
      billingAgreements,
      alternativePaymentGateway,
    });

    // Read at submit time: the pixel can set the _axwrt cookie after the page loads.
    const marketingTracking = getMarketingTracking(sfdcToken);
    if (marketingTracking) {
      paymentParams.marketingTracking = marketingTracking;
    }

    if (!payPalToken && isPayPalRedirectionFlow && billingAddress) {
      setPaymentInfo(paymentParams);
      submitPayPal();
      return;
    }

    submitPayment(paymentParams);
    setTriggerSubmit(false);
  }, [
    isAddressValid,
    isAddressValid2,
    payPalToken,
    billingAddress,
    billingAddress2,
    isPayPalRedirectionFlow,
    paymentInfo,
    contractId,
    documentNumber,
    documentNumber2,
    documentType,
    documentType2,
    isUpsell,
    sfdcToken,
    cpfNumber,
    selectedPaymentMethodOnFile,
    billingAgreements,
    submitPayment,
    submitPayPal,
    alternativePaymentGateway,
    setPaymentInfo,
  ]);

  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  useEffect(() => {
    if (isScreenLoading) {
      return;
    }

    if (payPalToken && !!billingAgreements) {
      handleSubmitRef.current?.();
      return;
    }

    const isTwoCreditCards =
      paymentInfo.paymentMethod === PaymentMethod.TWO_CREDIT_CARDS;
    const areAddressesValid = isTwoCreditCards
      ? isAddressValid && (isAddressValid2 ?? true)
      : isAddressValid;

    if ((contractAccepted || triggerSubmit) && areAddressesValid) {
      handleSubmitRef.current?.();
    }
  }, [
    isScreenLoading,
    payPalToken,
    billingAgreements,
    contractAccepted,
    triggerSubmit,
    isAddressValid,
    isAddressValid2,
    paymentInfo.paymentMethod,
    billingAddress,
    billingAddress2,
  ]);

  return { onSubmitButtonPress };
};
